import { Fixtuur } from '@fixtuur/backpack';
import * as Hooks from 'preact/hooks';
import * as Actions from '../../store/actions/app';
import * as Core from '../../store/core';
import { useDispatch, useSelector } from '../../store/redux';
import * as Selections from '../../store/selections';
import * as Selectors from '../../store/selectors';
import { useConfigurationFallbacks } from '../shared/useConfigurationFallbacks';
import { useGetCompatibleOptions } from '../shared/useGetOptions';
import { useCachingSelectionResolver } from './useCachingSelectionResolver';
import { Ui } from '@dora/vivian';

export const useSelectProduct = (
    selectableBuildProperties: Ui.PropertyOption.PropertyChoice[],
): {
    /** productId is null in the case of a NullProduct being clicked - in which case, null is a valid choice meaning no product should be selected for this part */
    proposeProductSelection: (
        productId: string | null,
        partId: Fixtuur.Part.Id,
        price: string | null,
    ) => void;
    isProposalResolving: boolean;
} => {
    const serviceConfig = useSelector(Selectors.Core.serviceConfig);
    const currentConfigurator = useSelector(Selectors.Initialisation.currentConfiguratorDefinition);
    const productSelections = useSelector(Selectors.Selections.productSelections);
    const buildPropertySelections = useSelector(Selectors.Selections.buildPropertySelections);

    const fixedSelections = useSelector(Selectors.Selections.groupingCodeSelections);

    const { fallbacks, isFallbacksLoading } = useConfigurationFallbacks();

    const dispatch = useDispatch();

    const [isProposalResolving, setIsProposalResolving] = Hooks.useState(false);

    const [pendingSelection, setPendingSelection] = Hooks.useState<{
        productId: string | null;
        partId: string;
        price: string | null;
    } | null>(null);

    const proposeProductSelection = (
        productId: string | null,
        partId: string,
        price: string | null,
    ) => {
        setPendingSelection({ productId, partId, price });
    };

    const compatibleOptions = useGetCompatibleOptions();

    const resolveSelection = useCachingSelectionResolver(
        serviceConfig,
        currentConfigurator?.configurationSpaceId,
        Fixtuur.Configuration.Selections.concat(
            productSelections || Fixtuur.Configuration.Selections.empty(),
            buildPropertySelections || Fixtuur.Configuration.Selections.empty(),
        ),
        compatibleOptions,
        fixedSelections ?? Fixtuur.Configuration.Selections.empty(),
        fallbacks,
        isFallbacksLoading,
    );

    // set product selections
    Hooks.useEffect(() => {
        if (!resolveSelection || !compatibleOptions || !pendingSelection || !serviceConfig) return;
        setPendingSelection(null);

        const { productId, partId, price } = pendingSelection;
        const productProperty = currentConfigurator
            ? currentConfigurator.partsMap[partId].productProperty
            : 'adjust';

        let compatible: boolean = true;

        if (
            !Fixtuur.Configuration.Options.isOptionIncluded(
                compatibleOptions,
                productProperty,
                productId,
            )
        ) {
            setIsProposalResolving(true);

            compatible = false;
        }

        dispatch(Actions.selectProductCardAction({ newItemId: productId, compatible, price }));

        resolveSelection(productProperty, productId)?.then(async resolvedPropertySelections => {
            // Check if any product selections were rejected due to incompatibility
            let removedProperties: Fixtuur.Configuration.Selections.Selections =
                Fixtuur.Configuration.Selections.empty();
            if (productSelections) {
                Object.keys(productSelections).forEach(newProperty => {
                    if (
                        newProperty !== productProperty &&
                        Fixtuur.Configuration.Selections.getByName(
                            productSelections,
                            newProperty,
                        ) !==
                            Fixtuur.Configuration.Selections.getByName(
                                resolvedPropertySelections,
                                newProperty,
                            )
                    ) {
                        const newPropertyValue = Fixtuur.Configuration.Selections.getByName(
                            productSelections,
                            newProperty,
                        );
                        // TODO error handling
                        if (
                            /** Be careful, null is a valid and expect value!! */
                            newPropertyValue === undefined
                        ) {
                            dispatch(
                                Core.error({
                                    message: `Selections does not include product property - this should never happen`,
                                    context: {
                                        type: 'log',
                                        location: 'useSelectProduct',
                                        additionalTags: {
                                            productSelections: JSON.stringify(productSelections),
                                            newProperty: JSON.stringify(newProperty),
                                        },
                                    },
                                }),
                            );
                            return;
                        }

                        removedProperties = Fixtuur.Configuration.Selections.addValue(
                            removedProperties,
                            newProperty,
                            newPropertyValue,
                        );
                    }
                });
            }
            setIsProposalResolving(false);

            /**
             * all product selections must exist because the proposal for resolving always includes
             * the the complete set of product selections
             */
            const productSelectionsByPart = currentConfigurator
                ? Fixtuur.Configuration.ProductSelectionsByPart.fromConfiguratorAndSelections(
                      currentConfigurator,
                      resolvedPropertySelections,
                  )
                : Fixtuur.Configuration.ProductSelectionsByPart.forConspiration(
                      Fixtuur.Configuration.Option.valueString(productId),
                  );

            dispatch(
                Selections.setProductSelectionsByPart({
                    productSelectionsByPart,
                }),
            );

            dispatch(
                Selections.updateBuildPropertySelections({
                    newSelections: Fixtuur.Configuration.Selections.keepKeys(
                        resolvedPropertySelections,
                        selectableBuildProperties.map(sBP => sBP.name),
                    ),
                }),
            );
        });
    }, [pendingSelection, resolveSelection, currentConfigurator, serviceConfig]);

    return {
        proposeProductSelection,
        isProposalResolving,
    };
};
